import { useEffect, useCallback, useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Pagination } from '../ui/pagination';
import {
  Download,
  Loader2,
  AlertCircle,
  ChevronDown,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { drilldownAPI } from '../../lib/api';
import { useView } from '../../contexts/ViewContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import type { DrilldownHeadline } from '../../contexts/DrilldownContext';
import {
  OTD_TABS,
  COMMON_LINKABLE_FIELDS,
  formatDrilldownValue,
  createFieldLink,
  createOTDStatusBadge,
  downloadExportedData,
  type DrilldownField,
  getRowDrillUrl,
  rowDrillProps,
  ROW_DRILL_CLASS,
} from './drilldownUtils';
import {
  exportTruncationMessage,
  readExportTruncation,
  type ExportTruncation,
} from '../../lib/exportTruncation';
import ExportTruncationBanner from '../export/ExportTruncationBanner';
import { pageCountFromResponse, type PageSizeMemory } from '../../hooks/usePagination';
import { OtifReviewStatusBadge } from '../purchasing/otifDecision';

interface OTDDrilldownModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters?: Record<string, unknown>;
  contextLabel?: string;
  /** CR-7261: the chart figure that was clicked, shown beside the context label. */
  headline?: DrilldownHeadline;
  initialTab?: 'on_time' | 'early' | 'late' | 'total';
}

export default function OTDDrilldownModal({
  isOpen,
  onClose,
  filters = {},
  contextLabel,
  headline,
  initialTab,
}: OTDDrilldownModalProps) {
  const { viewLevel, getViewQueryParams } = useView();
  const { formatDate } = useFormatters();

  const [activeTab, setActiveTab] = useState<string>(initialTab || 'on_time');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [records, setRecords] = useState<Array<Record<string, unknown>>>([]);
  const [fields, setFields] = useState<DrilldownField[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const servedPageRef = useRef<PageSizeMemory | undefined>(undefined);
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const [pageSize, setPageSize] = useState(25);
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  // CR-7432: counts from the last export, when it was cut short by the row cap.
  const [truncation, setTruncation] = useState<ExportTruncation | null>(null);

  // CR-5766: Request counter to prevent stale API responses from overwriting newer data
  const fetchRequestIdRef = useRef(0);

  const fetchTabCounts = useCallback(async () => {
    const counts: Record<string, number> = {};
    const viewParams = getViewQueryParams();

    for (const tab of OTD_TABS) {
      try {
        const response = await drilldownAPI.getDetails('otd_grns', {
          ...filters,
          ...tab.filter,
          view_level: viewLevel,
          ...viewParams,
          page: 1,
          page_size: 1,
        });
        counts[tab.key] = response.data.count;
      } catch {
        counts[tab.key] = 0;
      }
    }

    setTabCounts(counts);
  }, [filters, viewLevel, getViewQueryParams]);

  const fetchData = useCallback(async () => {
    // CR-5766: Increment request ID to track this specific request
    const requestId = ++fetchRequestIdRef.current;

    setLoading(true);
    setError(null);

    const activeTabConfig = OTD_TABS.find(t => t.key === activeTab);
    const tabFilter = activeTabConfig?.filter || {};
    const viewParams = getViewQueryParams();

    try {
      const response = await drilldownAPI.getDetails('otd_grns', {
        ...filters,
        ...tabFilter,
        view_level: viewLevel,
        ...viewParams,
        page: currentPage,
        page_size: pageSize,
      });

      // CR-5766: Only update state if this is still the latest request
      // This prevents stale responses from overwriting newer data
      if (requestId !== fetchRequestIdRef.current) {
        return; // A newer request was made, ignore this stale response
      }

      const { results, count } = response.data;
      setRecords(results.records);
      setFields(results.fields);
      setTotalCount(count);
      // CR-7279: page count from what the server served, not what was asked for.
      const paged = pageCountFromResponse(count, pageSize, results.records.length, currentPage, servedPageRef.current);
      servedPageRef.current = paged.memory;
      setTotalPages(paged.totalPages);
    } catch (err: unknown) {
      // CR-5766: Only show error if this is still the latest request
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to load OTD data');
    } finally {
      // CR-5766: Only clear loading if this is still the latest request
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [filters, activeTab, currentPage, pageSize, viewLevel, getViewQueryParams]);

  // CR-5766: Fix race condition - set activeTab FIRST, then fetch data
  // This effect handles modal open/close and initialTab changes
  useEffect(() => {
    if (isOpen) {
      // Set tab first before any fetching
      setActiveTab(initialTab || 'on_time');
      setCurrentPage(1);
    } else {
      // Reset state when modal closes
      setActiveTab(initialTab || 'on_time');
      setCurrentPage(1);
      setRecords([]);
    }
  }, [isOpen, initialTab]);

  // CR-5766: Fetch data only after activeTab is set - use separate effect
  // This ensures we always fetch with the correct activeTab value
  useEffect(() => {
    if (isOpen) {
      fetchTabCounts();
      fetchData();
    }
  }, [isOpen, activeTab, fetchTabCounts, fetchData]);

  // Reset state when tab changes
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
    }
    // CR-7432: the banner names a count for the cohort it was exported from.
    // Once the tab moves, those numbers describe a file nobody is looking at
    // any more.
    setTruncation(null);
  }, [activeTab, isOpen]);

  const renderCellValue = (fieldKey: string, value: unknown, record: Record<string, unknown>) => {
    const formattedValue = formatDrilldownValue(value, formatDate, record);
    const linkConfig = COMMON_LINKABLE_FIELDS[fieldKey];

    if (linkConfig && value) {
      const url = linkConfig.urlPattern(String(value), record);
      return createFieldLink(url, formattedValue);
    }

    // CR-7418 (Aaron, 19 Sep 2026): *"you need to make sure the badge format
    // and colour carries to all modals for consistency"*. This drill rendered
    // the review status as plain text while the OTIF drill beside it rendered
    // a badge — the registry gives every modal the same field and each one
    // decides how to draw it, so a new column is only styled where somebody
    // remembers to style it.
    if (fieldKey === 'otif_review' || fieldKey === 'po_line__otif_review') {
      return <OtifReviewStatusBadge value={value} />;
    }

    if (fieldKey === 'otd_status') {
      return createOTDStatusBadge(formattedValue);
    }

    return formattedValue;
  };

  const handleExport = async (format: 'csv' | 'xlsx') => {
    setExporting(true);
    setShowExportMenu(false);

    const activeTabConfig = OTD_TABS.find(t => t.key === activeTab);
    const tabFilter = activeTabConfig?.filter || {};
    const viewParams = getViewQueryParams();

    try {
      const response = await drilldownAPI.exportData('otd_grns', format, {
        ...filters,
        ...tabFilter,
        view_level: viewLevel,
        ...viewParams,
      });

      downloadExportedData(response.data, `otd_grns_${activeTab}_export`, format);

      // CR-7432: say when the row cap cut the file short — warn only, so a
      // complete export is as quiet as before.
      const truncated = readExportTruncation(response);
      setTruncation(truncated);
      if (truncated) toast.warning(exportTruncationMessage(truncated));
    } catch (err: unknown) {
      console.error('Export failed:', err);
      toast.error(`Export failed. The ${format.toUpperCase()} file could not be generated — please try again.`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-7xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl">
                OTD Analysis
                {contextLabel && (
                  <span className="text-muted-foreground font-normal ml-2">
                    - {contextLabel}
                  </span>
                )}
                {headline && (
                  <span className="text-muted-foreground font-normal ml-2" data-testid="drilldown-headline">
                    · {headline.label} {headline.value}
                  </span>
                )}
              </DialogTitle>
              <DialogDescription>
                Drill down into On-Time Delivery performance by delivery status
              </DialogDescription>
            </div>
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={exporting || records.length === 0}
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export
                <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
              {showExportMenu && (
                <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border z-50">
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Export as CSV
                  </button>
                  <button
                    onClick={() => handleExport('xlsx')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    Export as Excel
                  </button>
                </div>
              )}
            </div>
          </div>
          <ExportTruncationBanner
            truncation={truncation}
            onDismiss={() => setTruncation(null)}
          />
        </DialogHeader>

        {/* Tab Headers with Counts */}
        <div className="border-b">
          <div className="flex">
            {OTD_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <div className="flex flex-col items-center">
                  <span className="text-lg font-bold">
                    {tabCounts[tab.key]?.toLocaleString() ?? '-'}
                  </span>
                  <span className="text-xs mt-0.5">{tab.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Content Area - min-h prevents layout shift during loading that causes visual jerking */}
        <div className="flex-1 min-h-[400px] overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading data...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-red-500">
              <AlertCircle className="h-8 w-8 mb-2" />
              <p>{error}</p>
              <Button variant="outline" size="sm" onClick={fetchData} className="mt-4">
                Try Again
              </Button>
            </div>
          ) : records.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <p>No records found for this filter</p>
            </div>
          ) : (
            <div>
              <table className="w-full">
                <thead className="bg-muted/50 sticky top-0 z-10">
                  <tr>
                    {fields.map((field) => (
                      <th
                        key={field.field}
                        className="text-left py-3 px-4 text-sm font-medium whitespace-nowrap"
                      >
                        {field.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {records.map((record, idx) => (
                    <tr
                      key={String(record['id'] ?? idx)}
                      className={`border-b hover:bg-muted/30 transition-colors ${getRowDrillUrl(record) ? ROW_DRILL_CLASS : ''}`}
                      {...rowDrillProps(getRowDrillUrl(record))}
                    >
                      {fields.map((field) => (
                        <td
                          key={field.field}
                          className="py-3 px-4 text-sm whitespace-nowrap"
                        >
                          {renderCellValue(field.field, record[field.field], record)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && !error && totalPages > 1 && (
          <div className="flex-shrink-0 -mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              itemsPerPage={pageSize} servedPerPage={servedPageRef.current?.served}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
              itemLabel="GRN records"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
